import { supabase } from './supabaseClient';
import {
  OrganizationInvitation,
  InvitationCredits,
  CreateInvitationRequest,
  AcceptInvitationRequest,
  InvitationStatus,
  InvitationRole,
} from '../types/invitation';
import { User, UserRole } from '../types';
import { apiService } from './api';

// Helper to generate secure token
function generateToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const chars = Array.from(array, byte => String.fromCharCode(byte)).join('');
  return btoa(chars)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

// Map database invitation to frontend type
function mapDbInvitationToFrontend(dbInv: any): OrganizationInvitation {
  return {
    id: dbInv.id,
    organizationId: dbInv.organization_id,
    invitedBy: dbInv.invited_by,
    email: dbInv.email,
    role: dbInv.role as InvitationRole,
    token: dbInv.token,
    status: dbInv.status as InvitationStatus,
    expiresAt: dbInv.expires_at,
    acceptedAt: dbInv.accepted_at,
    acceptedBy: dbInv.accepted_by,
    createdAt: dbInv.created_at,
    updatedAt: dbInv.updated_at,
  };
}

// Map database credits to frontend type
function mapDbCreditsToFrontend(dbCredits: any): InvitationCredits {
  const remaining = (dbCredits.total_credits || 0) - (dbCredits.used_credits || 0);
  return {
    id: dbCredits.id,
    organizationId: dbCredits.organization_id,
    totalCredits: dbCredits.total_credits || 0,
    usedCredits: dbCredits.used_credits || 0,
    remainingCredits: remaining,
    updatedAt: dbCredits.updated_at,
  };
}

export const invitationService = {
  /**
   * Create a new invitation
   * Checks credits, generates token, and creates invitation record
   */
  createInvitation: async (
    organizationId: string,
    request: CreateInvitationRequest
  ): Promise<OrganizationInvitation> => {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Check if user has permission (admin or superadmin)
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, organization_id')
      .eq('id', user.id)
      .single();

    if (!profile || (profile.role !== 'admin' && profile.role !== 'superadmin')) {
      throw new Error('Insufficient permissions to create invitations');
    }

    // Check remaining credits
    const credits = await invitationService.getInvitationCredits(organizationId);
    if (credits.remainingCredits <= 0) {
      throw new Error('No remaining invitation credits');
    }

    // Check if email already has a pending invitation
    const { data: existingInvitation } = await supabase
      .from('organization_invitations')
      .select('id')
      .eq('email', request.email)
      .eq('organization_id', organizationId)
      .eq('status', 'pending')
      .single();

    if (existingInvitation) {
      throw new Error('A pending invitation already exists for this email');
    }

    // Generate token
    const token = generateToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now

    // Create invitation
    const { data, error } = await supabase
      .from('organization_invitations')
      .insert({
        organization_id: organizationId,
        invited_by: user.id,
        email: request.email,
        role: request.role,
        token,
        status: 'pending',
        expires_at: expiresAt.toISOString(),
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }

    // Use a credit
    const { error: creditError } = await supabase.rpc('use_invitation_credit', {
      p_organization_id: organizationId,
    });

    if (creditError) {
      console.error('Error using invitation credit:', creditError);
      // Rollback invitation creation
      await supabase
        .from('organization_invitations')
        .delete()
        .eq('id', data.id);
      throw new Error('Failed to use invitation credit');
    }

    // Send invitation email (non-blocking - don't fail if email fails)
    try {
      await apiService.email.sendInvitation(token);
    } catch (emailError) {
      console.error('Failed to send invitation email:', emailError);
      // Continue - invitation was created successfully even if email failed
    }

    return mapDbInvitationToFrontend(data);
  },

  /**
   * Get all invitations for an organization
   */
  getOrganizationInvitations: async (
    organizationId: string,
    status?: InvitationStatus
  ): Promise<OrganizationInvitation[]> => {
    let query = supabase
      .from('organization_invitations')
      .select('*')
      .eq('organization_id', organizationId)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching invitations:', error);
      throw error;
    }

    return (data || []).map(mapDbInvitationToFrontend);
  },

  /**
   * Get invitation by token (for acceptance flow)
   * Uses a SECURITY DEFINER RPC so unauthenticated users can look up their invitation.
   */
  getInvitationByToken: async (token: string): Promise<OrganizationInvitation | null> => {
    const { data, error } = await supabase.rpc('get_invitation_by_token', {
      p_token: token,
    });

    if (error) {
      console.error('Error fetching invitation by token:', error);
      throw error;
    }

    // RPC returns null if not found, expired, or not pending
    if (!data) {
      return null;
    }

    const inv = data as Record<string, unknown>;
    return {
      id: inv.id as string,
      organizationId: inv.organizationId as string,
      email: inv.email as string,
      role: inv.role as string,
      status: inv.status as string,
      token,
      expiresAt: inv.expiresAt as string,
      createdAt: inv.createdAt as string,
    } as OrganizationInvitation;
  },

  /**
   * Accept an invitation
   * Creates user account and links to organization
   */
  acceptInvitation: async (request: AcceptInvitationRequest): Promise<User> => {
    // Call auth-service edge function (unauthenticated — token is the proof of identity)
    const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
    const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

    const response = await fetch(`${SUPABASE_URL}/functions/v1/auth-service/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': ANON_KEY,
        'Authorization': `Bearer ${ANON_KEY}`,
      },
      body: JSON.stringify({
        token: request.token,
        password: request.password,
        firstName: request.firstName,
        lastName: request.lastName,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Error al aceptar la invitación');
    }

    // Map role for frontend
    const frontendRoleMap: Record<string, UserRole> = {
      lawyer: 'regular',
      assistant: 'regular',
      admin: 'admin',
    };

    return {
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      role: frontendRoleMap[data.user.role] || 'regular',
      organizationId: data.user.organizationId,
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  /**
   * Cancel a pending invitation
   */
  cancelInvitation: async (invitationId: string): Promise<void> => {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get invitation to check permissions
    const { data: invitation } = await supabase
      .from('organization_invitations')
      .select('organization_id, status')
      .eq('id', invitationId)
      .single();

    if (!invitation) {
      throw new Error('Invitation not found');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Only pending invitations can be cancelled');
    }

    // Check permissions
    const { data: profile } = await supabase
      .from('profiles')
      .select('role, organization_id')
      .eq('id', user.id)
      .single();

    if (!profile || (profile.role !== 'admin' && profile.role !== 'superadmin')) {
      throw new Error('Insufficient permissions');
    }

    if (profile.role !== 'superadmin' && profile.organization_id !== invitation.organization_id) {
      throw new Error('Insufficient permissions');
    }

    // Cancel invitation
    const { error } = await supabase
      .from('organization_invitations')
      .update({ status: 'cancelled' })
      .eq('id', invitationId);

    if (error) {
      console.error('Error cancelling invitation:', error);
      throw error;
    }

    // Refund credit - fetch current value first, then update
    const { data: creditsData } = await supabase
      .from('organization_invitation_credits')
      .select('used_credits')
      .eq('organization_id', invitation.organization_id)
      .single();

    if (creditsData && creditsData.used_credits > 0) {
      const { error: refundError } = await supabase
        .from('organization_invitation_credits')
        .update({
          used_credits: creditsData.used_credits - 1,
          updated_at: new Date().toISOString(),
        })
        .eq('organization_id', invitation.organization_id);

      if (refundError) {
        console.error('Error refunding credit:', refundError);
        // Don't throw - credit refund is not critical
      }
    }
  },

  /**
   * Get invitation credits for an organization
   */
  getInvitationCredits: async (organizationId: string): Promise<InvitationCredits> => {
    const { data, error } = await supabase
      .from('organization_invitation_credits')
      .select('*')
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No credits record exists - return default
        return {
          id: '',
          organizationId,
          totalCredits: 0,
          usedCredits: 0,
          remainingCredits: 0,
          updatedAt: new Date().toISOString(),
        };
      }
      console.error('Error fetching credits:', error);
      throw error;
    }

    return mapDbCreditsToFrontend(data);
  },

  /**
   * Initialize invitation credits for an organization
   * Called when organization is created or plan is selected
   */
  initializeInvitationCredits: async (
    organizationId: string,
    totalCredits: number
  ): Promise<void> => {
    const { error } = await supabase.rpc('initialize_invitation_credits', {
      p_organization_id: organizationId,
      p_total_credits: totalCredits,
    });

    if (error) {
      console.error('Error initializing credits:', error);
      throw error;
    }
  },

  /**
   * Refresh invitation credits when plan changes
   */
  refreshInvitationCredits: async (
    organizationId: string,
    newTotalCredits: number
  ): Promise<void> => {
    const { error } = await supabase.rpc('refresh_invitation_credits', {
      p_organization_id: organizationId,
      p_new_total: newTotalCredits,
    });

    if (error) {
      console.error('Error refreshing credits:', error);
      throw error;
    }
  },

  /**
   * Resend invitation email
   */
  resendInvitationEmail: async (invitationId: string): Promise<void> => {
    const { data: invitation, error } = await supabase
      .from('organization_invitations')
      .select('email, token, status')
      .eq('id', invitationId)
      .single();

    if (error || !invitation) {
      throw new Error('Invitation not found');
    }

    if (invitation.status !== 'pending') {
      throw new Error('Can only resend pending invitations');
    }

    // Resend invitation email
    try {
      await apiService.email.sendInvitation(invitation.token);
    } catch (emailError) {
      console.error('Failed to resend invitation email:', emailError);
      throw new Error('Failed to resend invitation email');
    }
  },
};
